import { useEffect, useRef } from 'react';

export interface ServerEvent {
  type:
    | 'conversation.created'
    | 'conversation.updated'
    | 'message.created'
    | 'message.updated'
    | 'message.status'
    | 'note.created'
    | 'reaction.updated'
    | 'typing.start'
    | 'typing.stop';
  conversationId: string;
  team: string;
  payload: unknown;
}

type Listener = (event: ServerEvent) => void;

class WsClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private reconnectAttempts = 0;
  private subscribedTopics = new Set<string>();

  connect(): void {
    // Auch CONNECTING abfangen: connect() wird aus mehreren Effekten gerufen
    // (App + jedes useWsEvent). Ohne diese Prüfung entstünden überlappende Sockets;
    // der open-Handler eines alten Sockets würde dann auf dem neuen, noch
    // CONNECTING-Socket senden und werfen.
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    )
      return;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws`);
    this.ws = ws;
    ws.addEventListener('open', () => {
      this.reconnectAttempts = 0;
      // Nur senden, wenn dieser Socket noch der aktuelle und OPEN ist.
      if (this.ws !== ws || ws.readyState !== WebSocket.OPEN) return;
      for (const t of this.subscribedTopics) {
        ws.send(JSON.stringify({ type: 'subscribe', topic: t }));
      }
    });
    ws.addEventListener('message', (e) => {
      try {
        const event = JSON.parse(e.data) as ServerEvent;
        for (const l of this.listeners) l(event);
      } catch {
        /* ignore */
      }
    });
    ws.addEventListener('close', () => {
      // Nur zurücksetzen, wenn dieser Socket noch der aktuelle ist (kein Reconnect-Sturm).
      if (this.ws !== ws) return;
      this.ws = null;
      const delay = Math.min(30_000, 1_000 * 2 ** this.reconnectAttempts++);
      setTimeout(() => this.connect(), delay);
    });
  }

  /**
   * Sendet nur, wenn der Socket OPEN ist. Während CONNECTING würde `ws.send()`
   * sonst `InvalidStateError` werfen — und ein Throw aus dem aufrufenden React-Effekt
   * reißt ohne ErrorBoundary den gesamten Baum auf Weiß. Der `open`-Handler flusht
   * alle in `subscribedTopics` gemerkten Topics nach, sobald die Verbindung steht.
   */
  private safeSend(payload: object): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  subscribe(topic: string): void {
    this.subscribedTopics.add(topic);
    this.safeSend({ type: 'subscribe', topic });
  }

  unsubscribe(topic: string): void {
    this.subscribedTopics.delete(topic);
    this.safeSend({ type: 'unsubscribe', topic });
  }

  onEvent(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export const wsClient = new WsClient();

export function useWsEvent(handler: Listener): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    wsClient.connect();
    return wsClient.onEvent((e) => ref.current(e));
  }, []);
}
